/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {DeviceFile} from '@ion/core/src/types/entities';

/**
 * Utility to manage file selection state and prevent unwanted clearing
 */
export class FileSelectionManager {
  private static userSelections = new Map<string, DeviceFile>();
  
  /**
   * Mark a file as user-selected for a specific task
   */
  static markAsUserSelected(taskId: string, file: DeviceFile): void {
    this.userSelections.set(taskId, file);
  }
  
  /**
   * Check if a file was user-selected for a specific task
   */
  static isUserSelected(taskId: string): boolean {
    return this.userSelections.has(taskId);
  }
  
  /**
   * Get the user-selected file for a specific task
   */
  static getUserSelection(taskId: string): DeviceFile | undefined {
    return this.userSelections.get(taskId);
  }
  
  /**
   * Clear user selection for a specific task
   */
  static clearUserSelection(taskId: string): void {
    this.userSelections.delete(taskId);
  }
  
  /**
   * Clear all user selections
   */
  static clearAllSelections(): void {
    this.userSelections.clear();
  }
  
  /**
   * Check if a file should be preserved during updates
   */
  static shouldPreventAutomaticOverwrite(taskId: string, currentFile: string): boolean {
    const userSelection = this.getUserSelection(taskId);
    return userSelection?.name === currentFile || this.isUserSelected(taskId);
  }
}

export default FileSelectionManager;
